import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './SignUpForm.module.css';

import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  apiSlice,
  useValidateStudentMutation,
  useValidateTeacherMutation,
} from '../../../../services/api';

import {
  SignupForm,
  RegisteredName,
  SignupSubmitForm,
  SignUp,
} from '../../../../types/login';

import Complete from '../../Complete';
import SelectBox from '../../../../components/SelectBox';
import Icon from '../../../../components/Icon';
import TermsItem from '../TermsItem';

import {
  createForm,
  limitMaxLen,
  regExName,
  regExPwd,
  removeCharacters,
  removeNoAlphabet,
} from '../../../../utils/util';

import {
  ADDITIONAL_INFO,
  CHECK_BIRTH_YEAR,
  EMAIL,
  ENTER,
  ENTER_EMAIL_MESSAGE,
  ENTER_MORE_LETTERS,
  PASSWORD_PATTERN,
  ID,
  NAME,
  NICKNAME,
  NOT_MATCH_PWD,
  PASSWORD,
  PREV,
  PWD_VALIDATION_MSG,
  REENTER,
  REQUIRED_INFO,
  SIGNUP,
  SIGNUP_COMPLETE,
  TERMS,
  CONFIRM,
  WELCOME,
  YEAR_OF_BIRTH,
  TEACHER,
  SCHOOL_GRADE,
  SUBJECT,
  ACCEPT_ALL_TERMS,
  SCHOOL_NAME,
  REGION,
  DEPARTMENT,
  ENTER_DEPARTMENT_TYPE,
  ENTER_GRADE_TYPE,
  ENTER_SCHOOL_NAME,
  ENTER_SCHOOL_TYPE,
  ENTER_REGION,
  SELECT_GENDER,
  ENTER_NICKNAME,
  INVALID_EMAIL,
  PROFANITY_WARNING_MESSAGE,
  LEGAL_REPRESENTATIVE_APPROVAL,
  LEGAL_REPRESENTATIVE_APPROVAL_DESC,
  MOBILE_AUTHENTICATION,
  COMPLETED_MOBILE_AUTHENTICATION,
  SIGN_UP_UNDER_14,
  SIGN_UP_OVER_14,
  BIRTHDATE_PLACEHOLDER,
  BIRTHDATE_REQUIRED_MESSAGE,
  MALE,
  FEMALE,
  DAD,
  MOM,
  MOBILE_NUMBER_DESC,
  ENTER_MOBILE_ALERT,
  ENTER_NAME_ALERT,
  ENTER_BIRTHDATE_ALERT,
  SELECT_PARENT,
} from '../../../../constants';
import { useValidateProfanityMutation } from '../../../../services/common';
import { removeLocalStorage } from '../../../../utils/storage';
import { formatDate, getAge, todayNumber } from '../../../../utils/date';

export default function SignUpForm({
  form,
  submit,
  alias: defaultAlias,
}: SignUp) {
  const cx = classNames.bind(styles);
  const { state } = useLocation();
  const navigate = useNavigate();
  const isTeacher = state.type === 'teacher';
  const isMinor = state.type === 'minor';

  const [validate] = isTeacher
    ? useValidateTeacherMutation()
    : useValidateStudentMutation();
  const [validateProfanity] = useValidateProfanityMutation();
  const [getStudentCert] = apiSlice.useLazyGetStudentCertQuery();

  const [isComplete, setIsComplete] = useState(false);
  const [isAgreeAll, setIsAgreeAll] = useState(false);
  const [isCheckedList, setIsCheckedList] = useState([false, false, false]);
  const [isEnableSubmit, setIsEnableSubmit] = useState(false);

  const [isValidAccount, setIsValidAccount] = useState({
    email: !!state.userProfile,
    alias: true,
    nm: !!state.userProfile,
  });
  const [invalidMsg, setInvalidMsg] = useState({
    email: state?.userProfile?.email ?? '',
    alias: '',
  });
  const [hasLegalApproval, setHasLegalApproval] = useState(false);

  const { commonType, agreeList, alias } = form;
  const {
    departmentTypeList,
    gradeTypeList,
    regionTypeList,
    schoolTypeList,
    subjectTypeList,
    teacherTypeList,
  } = commonType;

  const signUpSchema = yup.object({
    email: yup
      .string()
      .required(INVALID_EMAIL)
      .test('email', '', (value, context) => isValidAccount.email),
    pwd: state.userProfile
      ? yup.string().matches(regExPwd, {
          message: PWD_VALIDATION_MSG,
          excludeEmptyString: true,
        })
      : yup
          .string()
          .matches(regExPwd, {
            message: PWD_VALIDATION_MSG,
            excludeEmptyString: true,
          })
          .required(PWD_VALIDATION_MSG),
    'confirm-password': yup
      .string()
      .oneOf([yup.ref('pwd')])
      [state.userProfile ? 'notRequired' : 'required'](NOT_MATCH_PWD),
    alias: yup.string()[isTeacher ? 'notRequired' : 'required'](ENTER_NICKNAME),
    nm: yup
      .string()
      .matches(regExName, {
        message: ENTER_MORE_LETTERS,
        excludeEmptyString: isTeacher,
      })
      [isTeacher ? 'notRequired' : 'required'](),

    birthDate: yup
      .date()
      .transform((currentValue, originalValue) => {
        if (originalValue.length < 8) {
          return undefined;
        }

        const date = new Date(
          originalValue.toString().replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3')
        );
        return isNaN(date.getTime()) ? undefined : date;
      })
      .test(
        'is-minor',
        isMinor ? SIGN_UP_OVER_14 : SIGN_UP_UNDER_14,
        (value) => {
          const age = getAge(value as Date);
          return isMinor
            ? age < 14
            : age >= 14 ||
                age < 0 ||
                (value as Date).getTime() - new Date().getTime() >= 0;
        }
      )
      .min(19200101, CHECK_BIRTH_YEAR)
      .max(todayNumber, CHECK_BIRTH_YEAR)
      .required(({ originalValue }) => {
        return formatDate(originalValue)
          ? CHECK_BIRTH_YEAR
          : BIRTHDATE_REQUIRED_MESSAGE;
      }),
    gender: yup.string()[isTeacher ? 'notRequired' : 'required'](SELECT_GENDER),
    ...(isTeacher
      ? { teacherTypeCd: yup.string().notRequired() }
      : { regionTypeCd: yup.string().required(ENTER_REGION) }),
    schoolTypeCd: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_SCHOOL_TYPE),
    schoolNm: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_SCHOOL_NAME),
    gradeTypeCd: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_GRADE_TYPE),
    ...(isTeacher
      ? { subjectTypeCd: yup.string().notRequired() }
      : { departmentTypeCd: yup.string().required(ENTER_DEPARTMENT_TYPE) }),
    agreeCdList: yup
      .array()
      .test(
        'agree',
        '',
        (value) =>
          value?.toString() === isRequiredTermsList?.toString() ||
          !value?.includes('')
      ),
    lrNm: yup.string()[isMinor ? 'required' : 'notRequired'](ENTER_NAME_ALERT),
    lrTel: yup
      .string()
      [isMinor ? 'required' : 'notRequired'](ENTER_MOBILE_ALERT),
    lrBirthDate: yup
      .string()
      [isMinor ? 'required' : 'notRequired'](ENTER_BIRTHDATE_ALERT),
    lrGender: yup.string()[isMinor ? 'required' : 'notRequired'](SELECT_PARENT),
    requestNo: yup.string()[isMinor ? 'required' : 'notRequired'](),
  });
  type FormData = yup.InferType<typeof signUpSchema>;

  const isRequiredTermsList = useMemo(
    () => agreeList?.map((terms) => (terms.requireYn ? terms.agreeCd : '')),
    [agreeList]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValidating },
    getValues,
    setValue,
    control,
    setFocus,
    setError,
    clearErrors,
    trigger,
  } = useForm<SignupForm>({
    mode: 'onBlur',
    resolver: yupResolver<any>(signUpSchema),
    defaultValues: {
      email: state?.userProfile?.email ?? '',
      pwd: '',
      'confirm-password': '',
      alias: (defaultAlias || alias) ?? '',
      nm: state?.userProfile?.nm ?? '',
      birthDate: formatDate(state?.userProfile?.birthDate, 3) ?? '',
      gender: state?.userProfile?.gender ?? '',
      ...(isTeacher ? { teacherTypeCd: null } : { regionTypeCd: null }),
      schoolTypeCd: null,
      schoolNm: '',
      gradeTypeCd: null,
      ...(isTeacher ? { subjectTypeCd: null } : { departmentTypeCd: null }),
      agreeCdList: [''],
      lrNm: '',
      lrTel: '',
      lrBirthDate: '',
      lrGender: '',
      requestNo: null,
    },
  });

  const onBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const name: RegisteredName = e.target.name as RegisteredName;
    const value = getValues(name);

    if (name === 'alias' || name === 'nm') {
      if (!value) {
        setIsValidAccount({ ...isValidAccount, [name]: false });
        return;
      }

      try {
        const res = await validateProfanity(value);
        if ('data' in res) {
          setIsValidAccount({ ...isValidAccount, [name]: res.data });
          if (!res.data) {
            setError(name, {
              type: 'filter',
              message: PROFANITY_WARNING_MESSAGE,
            });
            setFocus(name);
            return;
          } else {
            regExName.test(value) && clearErrors(name);
          }
        }
      } catch (error) {
        console.error('Fail to validate profanity', error);
      }
    }

    if (name === 'email' || name === 'alias') {
      if (!value) {
        setIsValidAccount({ ...isValidAccount, [name]: false });
        setInvalidMsg({ ...invalidMsg, [name]: '' });
        return;
      }

      try {
        const res = await validate({
          type: name,
          value,
        }).unwrap();

        setIsValidAccount({ ...isValidAccount, [name]: res.message === 'OK' });
        if (res.message !== 'OK') {
          setInvalidMsg({ ...invalidMsg, [name]: res.message });
          setError(name, { type: ' invalid' });
        } else {
          clearErrors(name);
        }
      } catch (error) {
        console.log('Fail to validate', error);
      }
    }
  };

  const onChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target;

    switch (name) {
      case 'alias':
        target.value = limitMaxLen(value, 10);
        break;
      case 'nm':
        setFocus('nm');
        target.value = limitMaxLen(removeNoAlphabet(value), 10);
        break;
      case 'birthDate':
        target.value = limitMaxLen(removeCharacters(value), 8);
        break;
      case 'schoolNm':
        target.value = limitMaxLen(value, 30);
        break;
      default:
        break;
    }
  };

  const onSubmit = async (data: FormData | { birthDate: string }) => {
    const copiedData = {
      ...data,
      birthDate: formatDate(data.birthDate, 1),
    } as SignupSubmitForm;
    delete copiedData['confirm-password'];

    if (copiedData.alias === '') {
      copiedData.alias = alias as string;
    } else {
      copiedData.alias = copiedData.alias.trim();
    }
    copiedData.agreeCdList = copiedData.agreeCdList?.filter(
      (index) => index !== ''
    );
    copiedData.oauthType = state.userProfile ? 1 : null;

    console.log('copies: ', copiedData);
    try {
      await submit(copiedData).unwrap();
      setIsComplete(true);
    } catch (error) {
      console.log('Failed to submit', error);
    }
  };

  const setInputValue = (name: RegisteredName, value: string) => {
    setValue(name, value);
    trigger(name);

    if (name.includes('agree')) {
      const isAllChecked = !getIsCheckedList().includes(false);

      setIsAgreeAll(isAllChecked);
      setIsCheckedList(getIsCheckedList());
    }
  };

  const agreeAllTerms = () => {
    agreeList.map((item, i) => {
      setValue(`agreeCdList.${i}`, !isAgreeAll ? item.agreeCd : '');
    });
    setIsAgreeAll(!isAgreeAll);
    setIsCheckedList(getIsCheckedList());
  };

  const getIsCheckedList = () =>
    getValues('agreeCdList').map((code: string) => !!code);

  const certificateNice = async () => {
    try {
      const res = await getStudentCert().unwrap();
      const { action, encData, integrityValue, tokenVersionId } = res.data;
      const option = `status=no, menubar=no, toolbar=no, resizable=no, width=500, height=600`;
      const popup = window.open('', 'niceCertification', option);

      const form = createForm(
        { target: 'niceCertification', method: 'POST', action },
        [
          { name: 'm', value: 'service' },
          { name: 'token_version_id', value: tokenVersionId },
          { name: 'enc_data', value: encData },
          { name: 'integrity_value', value: integrityValue },
        ]
      );

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error('Failed to certificate', error);
    }
  };

  const checkRequestNo = (e: StorageEvent) => {
    if (e.key === 'requestNo') {
      setValue('requestNo', e.newValue);
      trigger('requestNo');
      removeLocalStorage('requestNo');
    }
  };

  useEffect(() => {
    signUpSchema
      .isValid(getValues())
      .then((res) => {
        setIsEnableSubmit(res && !errors.alias && !errors.nm);
      })
      .catch((err) => console.log(`Check required values: ${err}`));
  });

  useEffect(() => {
    window.addEventListener('storage', checkRequestNo);
    return () => {
      window.removeEventListener('storage', checkRequestNo);
    };
  }, []);

  return (
    <>
      <h2>{isComplete ? WELCOME : SIGNUP}</h2>
      {isComplete ? (
        <Complete name="signup" />
      ) : (
        <div className={cx(state.type)}>
          <form>
            <div className={cx('input_area')}>
              <h3>
                {REQUIRED_INFO}
                <span className={cx('point')}></span>
              </h3>
              <div
                className={cx('input_box', {
                  'input--done': isValidAccount.email || !!state.userProfile,
                  checked: !isValidAccount.email && errors.email,
                })}
              >
                <span className={cx('blind')}>{`${ID} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${EMAIL} ${ENTER}`}
                  placeholder={ENTER_EMAIL_MESSAGE}
                  autoComplete="off"
                  {...register('email', {
                    onBlur: onBlur,
                  })}
                  disabled={!!state.userProfile}
                />
                {!isValidAccount.email && errors.email && (
                  <span className={cx('msg', 'invalid')}>
                    {invalidMsg.email || errors.email?.message}
                  </span>
                )}
              </div>

              {!state.userProfile && (
                <>
                  <div
                    className={cx('input_box', {
                      checked: errors['pwd'],
                      'input--done': getValues('pwd') && !errors['pwd'],
                    })}
                  >
                    <span className={cx('blind')}>
                      `${PASSWORD} ${ENTER}`
                    </span>
                    <input
                      type="password"
                      title={`${PASSWORD} ${ENTER}`}
                      placeholder={PASSWORD_PATTERN}
                      autoComplete="new-password"
                      {...register('pwd')}
                    />
                    {errors['pwd'] && (
                      <span className={cx('msg', 'invalid')}>
                        {errors['pwd']?.message?.toString()}
                      </span>
                    )}
                  </div>
                  <div
                    className={cx('input_box', {
                      checked: errors['confirm-password'],
                      'input--done':
                        getValues('confirm-password') &&
                        !errors['confirm-password'],
                    })}
                  >
                    <span className={cx('blind')}>
                      `${PASSWORD} ${REENTER}`
                    </span>
                    <input
                      type="password"
                      title={`${PASSWORD} ${CONFIRM}`}
                      placeholder={`${PASSWORD} ${CONFIRM}`}
                      autoComplete="new-password"
                      {...register('confirm-password')}
                    />
                    {errors['confirm-password'] && (
                      <span className={cx('msg', 'invalid')}>
                        {NOT_MATCH_PWD}
                      </span>
                    )}
                  </div>
                </>
              )}
              {isTeacher && (
                <div
                  className={cx('input_box', {
                    checked: errors.birthDate,
                    'input--done': getValues('birthDate') && !errors.birthDate,
                  })}
                >
                  <span
                    className={cx('blind')}
                  >{`${YEAR_OF_BIRTH} ${ENTER}`}</span>
                  <input
                    type="text"
                    title={`${YEAR_OF_BIRTH} ${ENTER}`}
                    placeholder={BIRTHDATE_PLACEHOLDER}
                    inputMode="numeric"
                    {...register('birthDate', { onChange: onChange })}
                  />
                  {errors.birthDate && (
                    <span className={cx('msg', 'invalid')}>
                      {errors.birthDate.message}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className={cx('input_area', { mt32: isTeacher })}>
              {isTeacher && <h3>{ADDITIONAL_INFO}</h3>}
              <div
                className={cx('input_box', {
                  checked:
                    (getValues('alias') && !isValidAccount.alias) ||
                    errors.alias,
                  'input--done': isValidAccount.alias && !errors.alias,
                })}
              >
                <span className={cx('blind')}>{`${NICKNAME} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${NICKNAME} ${ENTER}`}
                  placeholder={alias}
                  {...register('alias', { onBlur: onBlur, onChange: onChange })}
                />
                {(!isValidAccount.alias || errors.alias) && (
                  <span className={cx('msg', 'invalid')}>
                    {invalidMsg.alias || errors.alias?.message}
                  </span>
                )}
              </div>
              <div
                className={cx('input_box', {
                  checked: (getValues('nm') && !isValidAccount.nm) || errors.nm,
                  'input--done':
                    getValues('nm') && isValidAccount.nm && !errors.nm,
                })}
              >
                <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${NAME} ${ENTER}`}
                  placeholder={NAME}
                  {...register('nm', {
                    onBlur: onBlur,
                    onChange: onChange,
                  })}
                />
                {(!isValidAccount.nm || errors.nm) && (
                  <span className={cx('msg', 'invalid')}>
                    {errors.nm?.message?.toString()}
                  </span>
                )}
              </div>
              {!isTeacher && (
                <div className={cx('partition_box')}>
                  <div
                    className={cx('input_box', 'checked', {
                      'input--done':
                        getValues('birthDate') && !errors.birthDate,
                    })}
                  >
                    <span
                      className={cx('blind')}
                    >{`${YEAR_OF_BIRTH} ${ENTER}`}</span>
                    <input
                      type="text"
                      title={`${YEAR_OF_BIRTH} ${ENTER}`}
                      placeholder={BIRTHDATE_PLACEHOLDER}
                      inputMode="numeric"
                      {...register('birthDate', { onChange: onChange })}
                    />
                    {errors.birthDate && (
                      <span className={cx('msg', 'invalid')}>
                        {errors.birthDate.message}
                      </span>
                    )}
                  </div>
                  <div className={cx('sort_box')}>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="M"
                          {...register('gender')}
                        />
                        <span>{MALE}</span>
                      </label>
                    </div>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="F"
                          {...register('gender')}
                        />
                        <span>{FEMALE}</span>
                      </label>
                    </div>
                    <span className={cx('msg', 'invalid')}>
                      {errors.gender?.message}
                    </span>
                  </div>
                </div>
              )}
              <div
                className={cx('partition_box', {
                  invalid: errors.regionTypeCd,
                })}
              >
                <span className={cx('blind')}>
                  {isTeacher ? TEACHER : REGION}
                </span>
                <Controller
                  render={({ field, fieldState }) => (
                    <SelectBox
                      list={isTeacher ? teacherTypeList : regionTypeList}
                      name={isTeacher ? 'teacherTypeCd' : 'regionTypeCd'}
                      setValue={setInputValue}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                  name={isTeacher ? 'teacherTypeCd' : 'regionTypeCd'}
                  control={control}
                  defaultValue={null}
                ></Controller>
                {isTeacher && (
                  <div className={cx('sort_box')}>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="M"
                          {...register('gender')}
                        />
                        <span>{MALE}</span>
                      </label>
                    </div>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="F"
                          {...register('gender')}
                        />
                        <span>{FEMALE}</span>
                      </label>
                    </div>
                    <span className={cx('msg', 'invalid')}>
                      {errors.gender?.message}
                    </span>
                  </div>
                )}
              </div>

              <div className={cx('partition_box')}>
                <span className={cx('blind')}>{SCHOOL_GRADE}</span>
                <Controller
                  render={({ field, fieldState }) => (
                    <SelectBox
                      list={schoolTypeList}
                      name="schoolTypeCd"
                      setValue={setInputValue}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                  name="schoolTypeCd"
                  control={control}
                  defaultValue={null}
                ></Controller>

                <div
                  className={cx('input_box', {
                    checked: errors.schoolNm,
                    'input--done': getValues('schoolNm') && !errors.schoolNm,
                  })}
                >
                  <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                  <input
                    type="text"
                    title={`${NAME} ${ENTER}`}
                    placeholder={SCHOOL_NAME}
                    {...register('schoolNm', {
                      onChange: onChange,
                    })}
                  />
                  <span className={cx('msg', 'invalid')}>
                    {errors.schoolNm?.message}
                  </span>
                </div>
              </div>

              <div
                className={cx('partition_box', { invalid: errors.gradeTypeCd })}
              >
                <span className={cx('blind')}>
                  {isTeacher ? SUBJECT : DEPARTMENT}
                </span>
                <Controller
                  render={({ field, fieldState }) => (
                    <SelectBox
                      list={gradeTypeList}
                      name="gradeTypeCd"
                      setValue={setInputValue}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                  name="gradeTypeCd"
                  control={control}
                  defaultValue={null}
                ></Controller>

                <Controller
                  render={({ field, fieldState }) => (
                    <SelectBox
                      list={isTeacher ? subjectTypeList : departmentTypeList}
                      name={isTeacher ? 'subjectTypeCd' : 'departmentTypeCd'}
                      setValue={setInputValue}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                  name={isTeacher ? 'subjectTypeCd' : 'departmentTypeCd'}
                  control={control}
                  defaultValue={null}
                ></Controller>
              </div>
            </div>

            {isMinor && (
              <div className={cx('legal_sign')}>
                <h3>{LEGAL_REPRESENTATIVE_APPROVAL}</h3>

                <div
                  className={cx('input_box', {
                    checked: errors.lrNm,
                    'input--done': getValues('lrNm') && !errors.lrNm,
                  })}
                >
                  <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                  <input
                    type="text"
                    title={`${NAME} ${ENTER}`}
                    placeholder={NAME}
                    {...register('lrNm', {
                      onBlur: onBlur,
                      onChange: onChange,
                    })}
                  />
                  {errors.lrNm && (
                    <span className={cx('msg', 'invalid')}>
                      {errors.lrNm?.message?.toString()}
                    </span>
                  )}
                </div>
                <div
                  className={cx('input_box', {
                    checked: errors.lrTel,
                    'input--done': getValues('lrTel') && !errors.lrTel,
                  })}
                >
                  <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                  <input
                    type="number"
                    title={`${NAME} ${ENTER}`}
                    placeholder={MOBILE_NUMBER_DESC}
                    inputMode="numeric"
                    {...register('lrTel', {
                      onBlur: onBlur,
                      onChange: onChange,
                    })}
                  />
                  {errors.lrTel && (
                    <span className={cx('msg', 'invalid')}>
                      {errors.lrTel?.message?.toString()}
                    </span>
                  )}
                </div>
                <div className={cx('partition_box')}>
                  <div
                    className={cx('input_box', 'checked', {
                      'input--done':
                        getValues('lrBirthDate') && !errors.lrBirthDate,
                    })}
                  >
                    <span
                      className={cx('blind')}
                    >{`${YEAR_OF_BIRTH} ${ENTER}`}</span>
                    <input
                      type="text"
                      title={`${YEAR_OF_BIRTH} ${ENTER}`}
                      placeholder={BIRTHDATE_PLACEHOLDER}
                      inputMode="numeric"
                      {...register('lrBirthDate', { onChange: onChange })}
                    />
                    {errors.lrBirthDate && (
                      <span className={cx('msg', 'invalid')}>
                        {errors.lrBirthDate.message}
                      </span>
                    )}
                  </div>
                  <div className={cx('sort_box')}>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="M"
                          {...register('lrGender')}
                        />
                        <span>{DAD}</span>
                      </label>
                    </div>
                    <div className={cx('radio_wrap')}>
                      <label>
                        <input
                          type="radio"
                          className={cx('blind')}
                          value="F"
                          {...register('lrGender')}
                        />
                        <span>{MOM}</span>
                      </label>
                    </div>
                    <span className={cx('msg', 'invalid')}>
                      {errors.lrGender?.message}
                    </span>
                  </div>
                </div>

                <div className={cx('row')}>
                  <label htmlFor="legal-representative-approval">
                    <input
                      type="checkbox"
                      name="legal-representative-approval"
                      id="legal-representative-approval"
                      className={cx('blind')}
                      checked={hasLegalApproval}
                      onChange={({ target }) => {
                        setHasLegalApproval(target.checked);
                        !target.checked && setValue('requestNo', null);
                      }}
                    />
                    <span className={cx('clabel')}>
                      {LEGAL_REPRESENTATIVE_APPROVAL_DESC}
                    </span>
                  </label>
                </div>
                <button
                  type="button"
                  className={cx('btn', 'b_t', 'dull', {
                    'is--disabled': !hasLegalApproval,
                    on: getValues('requestNo'),
                  })}
                  onClick={certificateNice}
                >
                  <Icon
                    name="check"
                    classname={cx('svg_check')}
                    isDisabled={!hasLegalApproval}
                  ></Icon>
                  <span>
                    {getValues('requestNo')
                      ? COMPLETED_MOBILE_AUTHENTICATION
                      : MOBILE_AUTHENTICATION}
                  </span>
                </button>
              </div>
            )}

            <div className={cx('access_terms_box')}>
              <h3>{TERMS}</h3>
              <button
                type="button"
                className={cx('btn', 'b_t', 'dull', { on: isAgreeAll })}
                onClick={agreeAllTerms}
              >
                <Icon name="check" classname={cx('svg_check')}></Icon>
                <span>{ACCEPT_ALL_TERMS}</span>
              </button>

              <ul>
                {agreeList.map((item, i) => (
                  <Controller
                    key={item.agreeCd}
                    render={(field) => (
                      <TermsItem
                        {...field}
                        data={item}
                        setValue={setInputValue}
                        checked={isCheckedList[i]}
                      />
                    )}
                    name={`agreeCdList.${i}` as const}
                    control={control}
                    defaultValue=""
                  ></Controller>
                ))}
              </ul>
            </div>

            <div className={cx('btn_wrap', 'bottom', 'cell')}>
              <button
                type="button"
                className={cx('btn', 'b_t', 'dull', 's44')}
                onClick={() => navigate(-1)}
              >
                <span>{PREV}</span>
              </button>
              <button
                type="button"
                className={cx('btn', 'b_t', 's44', {
                  'is--disabled': !isEnableSubmit,
                })}
                onClick={handleSubmit(onSubmit)}
              >
                <span>{SIGNUP_COMPLETE}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
